/**
 * Rotas do sistema, em um só lugar. Migalhas e navegação usam estas funções — nunca strings soltas.
 */
export type TemplateTab =
  'editor' | 'versions' | 'compare' | 'procedures' | 'clinics' | 'history' | 'data'
export type IntegrationTab = 'overview' | 'permissions' | 'webhooks' | 'deliveries' | 'requests'

export const routes = {
  login: '/login',
  /** A clínica cria a própria conta (docs/areas/autenticacao.md, RN-AUT-26). */
  signup: '/signup',
  /** Troca obrigatória da senha temporária (docs/areas/autenticacao.md, RN-AUT-05). */
  newPassword: '/new-password',
  /** Aceite do convite: a pessoa chega pelo link do e-mail e cria a senha (RT-04). */
  invite: '/invite',
  /** Entrar com código de 6 dígitos enviado por e-mail: o login rápido, sem senha (T48). */
  signInCode: '/sign-in/code',
  /** Escolha da clínica ativa, para quem tem vínculo em mais de uma. */
  chooseClinic: '/choose-clinic',
  /**
   * O formulário de anamnese que a paciente responde pelo link, sem sessão (docs/api/anamnese.md):
   * o servidor monta o link como `<app>/anamnesis?t=<token>`.
   */
  anamnesisForm: '/anamnesis',
  /**
   * Consentimento OAuth do MCP (docs/api/mcp.md). O caminho é fixo: está no config.toml do Supabase
   * (`auth.oauth_server.authorization_url_path`) e chega com `?authorization_id=…`.
   */
  oauthConsent: '/oauth/consent',
  dashboard: '/dashboard',
  /** A conta da própria pessoa: acessos, aplicativos conectados, senha (docs/areas/autenticacao.md). */
  account: '/account',
  accountTab: (tab: 'sessions' | 'connections' | 'password') => `/account/${tab}`,
  /** Implantação da clínica (docs/areas/administracao.md, RN-ADM-80). */
  onboarding: '/onboarding',
  /** A implantação aberta numa etapa: é o link de cada etapa no dashboard (RN-ADM-90). */
  onboardingStep: (step: 'clinic_profile' | 'witnesses' | 'procedures' | 'review') =>
    `/onboarding?step=${step}`,
  patients: '/patients',
  newPatient: '/patients/new',
  /** O link público de cadastro da clínica, o QR e a peça para imprimir (RN-PAC-31, T37). */
  patientRegistrationLink: '/patients/registration-link',
  patient: (id: string) => `/patients/${id}`,
  patientTab: (id: string, tab: 'details' | 'anamneses' | 'documents' | 'treatment-plans') =>
    `/patients/${id}/${tab}`,
  patientAnamnesis: (patientId: string, anamnesisId: string) =>
    `/patients/${patientId}/anamneses/${anamnesisId}`,
  /** Os cadastros duplicados de um paciente, lado a lado, para unificar (RN-PAC-16 e 17). */
  patientDuplicates: (id: string) => `/patients/${id}/duplicates`,
  /** Um documento gerado aberto dentro da aba Documentos da ficha (RN-DOC-29, RN-DOC-34). */
  patientDocument: (patientId: string, appointmentId: string, documentId: string) =>
    `/patients/${patientId}/documents/${appointmentId}/${documentId}`,
  /** O fluxo de geração com anamnese (RN-DOC-18); `?appointment=<id>` retoma um rascunho ou abre um gerado. */
  generateDocuments: (patientId: string, appointmentId?: string) =>
    `/patients/${patientId}/generate-documents${appointmentId ? `?appointment=${appointmentId}` : ''}`,
  /** Os documentos avulsos (RN-DOC-07); mesma retomada por `?appointment=<id>`. */
  standaloneDocuments: (patientId: string, appointmentId?: string) =>
    `/patients/${patientId}/standalone-documents${appointmentId ? `?appointment=${appointmentId}` : ''}`,
  /** Adicionar documentos a um atendimento já enviado (RN-DOC-57): o mesmo fluxo, com `?addTo=<id>`. */
  addDocuments: (patientId: string, parentAppointmentId: string) =>
    `/patients/${patientId}/generate-documents?addTo=${parentAppointmentId}`,
  anamneses: '/anamneses',
  /** As perguntas da clínica no questionário de anamnese (gestora; RN-ANA-19). */
  anamnesisQuestionnaire: '/anamneses/questionnaire',
  signatures: '/signatures',
  /** A lista aberta num envio (o painel lateral), por atendimento (RN-ASS-12). */
  signature: (appointmentId: string) => `/signatures?request=${appointmentId}`,
  signaturesFiltered: (filter: 'pending' | 'awaiting_patient' | 'signed' | 'all') =>
    `/signatures?filter=${filter}`,
  treatmentPlans: '/treatment-plans',
  /** A lista já no segmento certo (o dashboard aponta para `?filter=pending`, RN-DSH-16). */
  treatmentPlansFiltered: (filter: 'open' | 'closed' | 'all' | 'pending' | 'archived') =>
    `/treatment-plans?filter=${filter}`,
  /** "+ Novo plano" (RN-PLA-03); da ficha da paciente, com ela já escolhida. */
  newTreatmentPlan: (patientId?: string) =>
    `/treatment-plans/new${patientId ? `?patientId=${patientId}` : ''}`,
  treatmentPlan: (id: string) => `/treatment-plans/${id}`,
  /** A ficha aberta numa aba: Informações gerais · Produtos · Fotos. */
  treatmentPlanTab: (id: string, tab: 'info' | 'products' | 'photos') =>
    `/treatment-plans/${id}?tab=${tab}`,
  /** A tela de regiões de aplicação de um produto do plano (RN-PLA-11). */
  treatmentPlanRegions: (id: string, productId: string) =>
    `/treatment-plans/${id}/products/${productId}/regions`,
  /**
   * A página pública do celular que leu o QR de fotos (docs/api/fotos.md): o servidor monta o link
   * como `<app>/photos?t=<token>`; sem sessão, como o formulário de anamnese.
   */
  photoSession: '/photos',
  admin: {
    clinicProfile: '/admin/clinic-profile',
    users: '/admin/users',
    newUser: '/admin/users/new',
    user: (id: string) => `/admin/users/${id}`,
    practitioners: '/admin/practitioners',
    /** A trilha de ações da clínica ativa (RN-ADM-60). */
    auditLog: '/admin/audit-log',
    newPractitioner: '/admin/practitioners/new',
    practitioner: (id: string) => `/admin/practitioners/${id}`,
    procedures: '/admin/procedures',
    newProcedure: '/admin/procedures/new',
    addProcedures: '/admin/procedures/add',
    importProcedures: '/admin/procedures/import',
    procedure: (id: string) => `/admin/procedures/${id}`,
    /** Os apps de integração da clínica, o MCP e a API (docs/areas/integracoes.md, T53). */
    integrations: '/admin/integrations',
    newIntegration: '/admin/integrations/new',
    integration: (id: string) => `/admin/integrations/${id}`,
    /** O app aberto numa aba: Visão geral · Permissões · Webhooks · Entregas · Chamadas. */
    integrationTab: (id: string, tab: IntegrationTab) => `/admin/integrations/${id}?tab=${tab}`,
    // Painel da Westhetics (root)
    /** A equipe da Westhetics: quem é root (RN-AUT-28 a 30). `:id` é a pessoa. */
    team: '/admin/team',
    newTeamMember: '/admin/team/new',
    teamMember: (id: string) => `/admin/team/${id}`,
    /** O monitoramento da equipe: o que a Westhetics faz sozinha por todas as clínicas (RN-ADM-94 a 99). */
    operations: '/admin/operations',
    clinics: '/admin/clinics',
    networks: '/admin/networks',
    newNetwork: '/admin/networks/new',
    network: (id: string) => `/admin/networks/${id}`,
    newClinic: '/admin/clinics/new',
    clinic: (id: string) => `/admin/clinics/${id}`,
    catalogProcedures: '/admin/catalog/procedures',
    newCatalogProcedure: '/admin/catalog/procedures/new',
    catalogProcedure: (id: string) => `/admin/catalog/procedures/${id}`,
    /** O questionário padrão de anamnese da Westhetics, versionado (root; RN-ANA-18). */
    questionnaire: '/admin/questionnaire',
    questionnaireVersion: (id: string) => `/admin/questionnaire/${id}`,
    templates: '/admin/templates',
    newTemplate: '/admin/templates/new',
    template: (id: string, tab: TemplateTab = 'editor') => `/admin/templates/${id}/${tab}`,
    publishTemplate: (id: string) => `/admin/templates/${id}/publish`,
  },
} as const
